/**
 * 统一导航配置管理
 * 完全依赖模块的 menu.ts 配置文件，动态加载所有模块菜单
 * @author 系统
 * @date 2024
 */

import type { NavigationItem, ModuleNavigation } from '@/types/navigation'
import type { MenuConfig } from '@/types/menu'

/**
 * 将MenuConfig转换为ModuleNavigation格式
 * @param menuConfig 菜单配置
 * @returns 导航配置
 */
const convertMenuToNavigation = (menuConfig: MenuConfig): ModuleNavigation => {
  const items: NavigationItem[] = []

  if (menuConfig.children && menuConfig.children.length > 0) {
    // 有子菜单的情况
    menuConfig.children.forEach((child, index) => {
      items.push({
        id: child.name,
        title: child.title,
        path: child.path || '',
        icon: child.icon,
        order: child.order || index + 1,
        permissions: child.permissions || [],
        module: menuConfig.name,
        hidden: child.hidden,
        affix: child.meta?.affix || false, // 从meta中提取affix属性
        meta: child.meta
      })
    })
  } else if (menuConfig.path) {
    // 一级菜单的情况，将自身作为菜单项
    items.push({
      id: menuConfig.name,
      title: menuConfig.title,
      path: menuConfig.path,
      icon: menuConfig.icon,
      order: menuConfig.order || 1,
      permissions: menuConfig.permissions || [],
      module: menuConfig.name,
      hidden: menuConfig.hidden,
      affix: menuConfig.meta?.affix || false, // 从meta中提取affix属性
      meta: menuConfig.meta
    })
  }

  return {
    name: menuConfig.name,
    title: menuConfig.title,
    icon: menuConfig.icon,
    order: menuConfig.order || 999,
    items
  }
}

// 动态导入所有模块的配置
const moduleConfigs = import.meta.glob('../modules/**/config/module.ts', { eager: true })

// 从模块中收集配置并转换为导航配置
const moduleNavigations: ModuleNavigation[] = []
try {
  Object.values(moduleConfigs).forEach((mod: any) => {
    if (mod?.default?.navigation) {
      // 处理ModuleConfig格式的配置
      const moduleConfig = mod.default
      if (moduleConfig.navigation.name && moduleConfig.navigation.title) {
        moduleNavigations.push(moduleConfig.navigation)
      }
    }
  })
} catch (error) {
  console.warn('Failed to load module configs:', error)
}

/**
 * 所有模块导航配置
 */
export const allNavigations: ModuleNavigation[] = moduleNavigations.sort((a, b) => (a.order || 999) - (b.order || 999))

/**
 * 获取所有导航项（扁平化）
 */
export const getAllNavigationItems = (): NavigationItem[] => {
  const items: NavigationItem[] = []

  allNavigations.forEach(module => {
    // 添加安全检查，确保module.items存在且为数组
    if (module.items && Array.isArray(module.items)) {
      module.items.forEach(item => {
        items.push(item)
        if (item.children) {
          items.push(...item.children)
        }
      })
    }
  })
  return items.sort((a, b) => (a.order || 999) - (b.order || 999))
}

/**
 * 获取固定标签页配置
 */
export const getAffixTabs = (): NavigationItem[] => {
  return getAllNavigationItems().filter(item => item.affix)
}

/**
 * 检查路径是否匹配（支持参数路径）
 * @param routePath 路由路径（可能包含参数，如 /user/:id）
 * @param currentPath 当前路径（实际路径，如 /user/123）
 * @returns 是否匹配
 */
const isPathMatch = (routePath: string, currentPath: string): boolean => {
  // 精确匹配
  if (routePath === currentPath) {
    return true
  }

  // 参数路径匹配
  const routeSegments = routePath.split('/')
  const currentSegments = currentPath.split('/')

  if (routeSegments.length !== currentSegments.length) {
    return false
  }

  return routeSegments.every((segment, index) => {
    // 如果是参数段（以:开头），则匹配任意值
    if (segment.startsWith(':')) {
      return true
    }
    // 否则必须精确匹配
    return segment === currentSegments[index]
  })
}

/**
 * 根据路径查找导航项
 */
export const findNavigationByPath = (path: string): NavigationItem | undefined => {
  return getAllNavigationItems().find(item => isPathMatch(item.path, path))
}

/**
 * 根据 ID 查找导航项
 */
export const findNavigationById = (id: string): NavigationItem | undefined => {
  return getAllNavigationItems().find(item => item.id === id)
}

/**
 * 根据模块名获取导航配置
 */
export const getNavigationByModule = (moduleName: string): ModuleNavigation | undefined => {
  return allNavigations.find(nav => nav.name === moduleName)
}

/**
 * 转换为菜单格式（兼容现有菜单组件）
 */
export const convertToMenuFormat = (navigation: ModuleNavigation) => {
  // 检查是否为一级菜单（模块本身就是菜单项）
  const isTopLevelMenu = navigation.items.length === 1 &&
    navigation.items[0].id === navigation.name &&
    navigation.items[0].path &&
    !navigation.items[0].children

  if (isTopLevelMenu) {
    // 一级菜单，直接显示模块配置的菜单名，不需要折叠
    const item = navigation.items[0]
    return {
      title: navigation.title, // 使用模块的title
      path: item.path,
      icon: navigation.icon, // 使用模块的icon
      permission: item.permissions?.[0],
      order: navigation.order
    }
  } else {
    // 有二级菜单的模块，按照折叠结构显示
    return {
      name: navigation.name,
      title: navigation.title,
      icon: navigation.icon,
      order: navigation.order,
      children: navigation.items.map(item => ({
        name: item.id,
        title: item.title,
        path: item.path,
        icon: item.icon,
        permissions: item.permissions
      }))
    }
  }
}

/**
 * 转换为标签页格式（兼容现有标签页组件）
 */
export const convertToTabFormat = (item: NavigationItem) => {
  return {
    path: item.path,
    title: item.title,
    icon: item.icon,
    affix: item.affix || false
  }
}
